import React, { useState, useMemo } from 'react'; // Import hooks
import {
  Box,
  HStack,
  VStack,
  Text,
  Tooltip,
  useColorModeValue,
  Flex,
  useBreakpointValue,
  Icon,
  Circle,
  Popover,             // <-- Add Popover imports
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Badge, // <-- Add Badge import
} from '@chakra-ui/react';
import { Star, CalendarBlank } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion'; // Import motion and AnimatePresence

// Helper function to format year for display
const formatYear = (year) => {
  if (year === null || year === undefined) return '?';
  if (year < 0) return `${Math.abs(year)} BCE`;
  if (year === 0) return '1 CE'; // Or handle 0 year specifically if needed
  return `${year} CE`;
};

// Helper function to extract year value
const getYearValue = (eraStringInput) => {
  if (!eraStringInput) return null;

  let eraString = eraStringInput;
  // Strip trailing '?' if present, for parsing
  if (eraString.endsWith('?')) {
    eraString = eraString.slice(0, -1);
  }
  
  // Special handling for "present" - use current year
  if (eraString.toLowerCase() === 'present') {
    return new Date().getFullYear();
  }
  
  const match = eraString.match(/(\d+)\s*(BCE|CE|BC|AD)?/i);
  if (!match) return null;
  let year = parseInt(match[1], 10);
  const era = (match[2] || '').toUpperCase();
  if (era === 'BCE' || era === 'BC') {
    year = -year;
  }
  return year;
};

// --- Horizontal Timeline (Desktop) ---
const HorizontalTimeline = ({ periods, mainPeriodIndex, onClick }) => {
  const timelineColor = useColorModeValue('gray.300', 'gray.600');
  const accentColor = useColorModeValue('brand.primary', 'brand.primary');
  const periodBgBase = useColorModeValue('white', 'gray.700'); // Use white for base bg
  const periodBgMain = useColorModeValue('rgba(193, 148, 52, 0.2)', 'rgba(214, 173, 96, 0.2)');
  const periodBorderBase = useColorModeValue('gray.200', 'gray.600');
  const periodBorderMain = useColorModeValue('rgba(193, 148, 52, 0.4)', 'rgba(214, 173, 96, 0.4)');
  const textColorMuted = useColorModeValue('text.muted', 'text.muted');
  const textColorDefault = useColorModeValue('text.default', 'text.default');

  // Calculate year range and check if proportional layout is possible
  const { earliestYear, latestYear, useProportionalLayout } = useMemo(() => {
    let minYear = null;
    let maxYear = null;
    periods.forEach(period => {
      const startYear = getYearValue(period.startEra);
      const endYear = getYearValue(period.endEra);
      if (startYear !== null) minYear = minYear === null ? startYear : Math.min(minYear, startYear);
      if (endYear !== null) maxYear = maxYear === null ? endYear : Math.max(maxYear, endYear);
      // If only one date exists, consider it for range calculation
      if (startYear !== null && endYear === null) maxYear = maxYear === null ? startYear : Math.max(maxYear, startYear);
      if (startYear === null && endYear !== null) minYear = minYear === null ? endYear : Math.min(minYear, endYear);
    });

    const proportional = minYear !== null && maxYear !== null;
    let paddedMin = minYear;
    let paddedMax = maxYear;

    if (proportional) {
      const range = maxYear - minYear;
      // Ensure padding doesn't create an infinite loop for single-year ranges
      const padding = range === 0 ? 50 : Math.max(50, range * 0.1);
      paddedMin -= padding;
      paddedMax += padding;
    }

    return {
      earliestYear: paddedMin,
      latestYear: paddedMax,
      useProportionalLayout: proportional,
    };
  }, [periods]);

  // Function to calculate percentage position
  const getPositionPercentage = (year) => {
    if (!useProportionalLayout || year === null) return null;
    const totalRange = latestYear - earliestYear;
    if (totalRange <= 0) return 50; // Default to center if range is zero or negative
    // Clamp percentage between 0 and 100
    return Math.max(0, Math.min(100, ((year - earliestYear) / totalRange) * 100));
  };

  // --- Overlap Detection and Level Assignment ---
  const periodLayouts = useMemo(() => {
    if (!periods || periods.length === 0) return [];

    const calculatedPeriods = periods
      .map((period, index) => {
        const startYear = getYearValue(period.startEra);
        const endYear = getYearValue(period.endEra);
        const isPoint = startYear === null || endYear === null || startYear === endYear;

        // Use start/end year if available, otherwise treat as point using whichever date exists
        const effectiveStartYear = isPoint ? (startYear ?? endYear) : startYear;
        const effectiveEndYear = isPoint ? (startYear ?? endYear) : endYear;

        // Calculate positions, handle potential nulls
        let startPerc = getPositionPercentage(effectiveStartYear);
        let endPerc = isPoint ? startPerc : getPositionPercentage(effectiveEndYear);

        // Ensure valid percentages and order
        if (startPerc === null && endPerc === null) return null; // Cannot position this period
        if (startPerc === null) startPerc = endPerc;
        if (endPerc === null) endPerc = startPerc;
        if (endPerc < startPerc) [startPerc, endPerc] = [endPerc, startPerc]; // Swap if needed

        const widthPerc = Math.max(0.5, endPerc - startPerc); // Minimum width for visibility

        return {
          ...period,
          index,
          startYear: effectiveStartYear, // Store calculated years
          endYear: effectiveEndYear,
          startPerc,
          endPerc,
          widthPerc,
          isPoint,
          level: 0, // Initial level
        };
      })
      .filter(p => p !== null) // Remove periods that couldn't be positioned
      .sort((a, b) => a.startPerc - b.startPerc || a.endPerc - b.endPerc); // Sort by start, then end

    // Assign levels to prevent overlap
    const levels = []; // Tracks the end percentage of the last item on each level
    calculatedPeriods.forEach(currentPeriod => {
      let assignedLevel = -1;
      for (let level = 0; level < levels.length; level++) {
        // Check if current period starts after the last period on this level ends (with a small gap)
        if (currentPeriod.startPerc >= (levels[level] ?? -Infinity) + 0.5) {
          assignedLevel = level;
          break;
        }
      }

      if (assignedLevel === -1) {
        // No suitable existing level found, add a new level
        assignedLevel = levels.length;
        levels.push(-Infinity); // Initialize new level end marker
      }

      currentPeriod.level = assignedLevel;
      // Update the end marker for this level
      levels[assignedLevel] = currentPeriod.endPerc;
    });

    return calculatedPeriods;
  }, [periods, mainPeriodIndex, useProportionalLayout, earliestYear, latestYear]); // Dependencies

  // --- Generate X-Axis Markers ---
  const axisMarkers = useMemo(() => {
    if (!useProportionalLayout) return [];
    const markers = [];
    const totalRange = latestYear - earliestYear;
    if (totalRange <= 0) return [];

    // Determine a reasonable interval (simplified)
    let interval = 1000;
    if (totalRange <= 200) interval = 25;
    else if (totalRange <= 1000) interval = 100;
    else if (totalRange <= 5000) interval = 500;

    const firstMarker = Math.ceil(earliestYear / interval) * interval;
    const lastMarker = Math.floor(latestYear / interval) * interval;

    for (let year = firstMarker; year <= lastMarker; year += interval) {
      // Avoid placing markers too close to the edges if they are the min/max
      const position = getPositionPercentage(year);
       if (position > 1 && position < 99) { // Only add markers comfortably within the range
           markers.push({
             year: year,
             label: formatYear(year),
             position: position,
           });
       }
    }
     // Always add start and end markers
     markers.unshift({ year: earliestYear, label: formatYear(earliestYear), position: 0 });
     markers.push({ year: latestYear, label: formatYear(latestYear), position: 100 });

    // Filter out duplicate labels potentially caused by padding
    const uniqueMarkers = [];
    const seenLabels = new Set();
    markers.sort((a,b) => a.position - b.position).forEach(marker => {
        if (!seenLabels.has(marker.label)) {
            uniqueMarkers.push(marker);
            seenLabels.add(marker.label);
        }
    });


    return uniqueMarkers;
  }, [useProportionalLayout, earliestYear, latestYear]);

  const levelHeight = 45; // Height allocated for each level + spacing
  const maxLevel = useMemo(() => periodLayouts.reduce((max, p) => Math.max(max, p.level), 0), [periodLayouts]);
  const totalHeight = 90 + (maxLevel * levelHeight); // Base height + height for levels
  const timelineYPosition = totalHeight - 45; // Position axis below the lowest level

  return (
    <Box w="100%" position="relative" pb={8} px={4} minH={`${totalHeight + 30}px`}> {/* Add padding bottom */}
      {/* Main timeline line */}
      <Box
        position="absolute"
        top={`${timelineYPosition}px`} // Position below levels
        left="0"
        right="0"
        height="3px"
        bg={timelineColor}
        borderRadius="full"
        zIndex={0} // Behind periods
      />

      {/* X-Axis Markers */}
      <Box position="absolute" top={`${timelineYPosition + 10}px`} left="0" right="0" zIndex={0}>
         {axisMarkers.map(marker => (
           <Box
             key={marker.year}
             position="absolute"
             left={`${marker.position}%`}
             transform="translateX(-50%)"
             textAlign="center"
           >
             {/* Line tick */}
             <Box w="1px" h="5px" bg={timelineColor} mx="auto" />
             <Text fontSize="10px" color={textColorMuted} mt={1} whiteSpace="nowrap">
               {marker.label}
             </Text>
           </Box>
         ))}
      </Box>

      {/* Timeline periods Container */}
      <Box position="relative" w="100%" h={`${totalHeight}px`}>
        <AnimatePresence>
          {periodLayouts.map((period) => {
            const isMain = period.index === mainPeriodIndex;
            // Calculate vertical position based on level, placing level 0 closest to the axis
            const topPosition = timelineYPosition - levelHeight - (period.level * levelHeight);

            return (
              <motion.div
                key={`${period.index}-${period.periodName}`}
                layout // Animate layout changes
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                style={{
                  position: 'absolute',
                  left: `${period.startPerc}%`,
                  width: period.isPoint ? 'auto' : `${period.widthPerc}%`,
                  top: `${topPosition}px`,
                  zIndex: isMain ? 3 : 2,
                  marginLeft: period.isPoint ? '-18px' : '0', // Center point markers (adjust based on size: 36px / 2)
                }}
              >
                <Popover trigger="hover" placement="top" openDelay={100} closeDelay={100} isLazy>
                  <PopoverTrigger>
                    <Box
                      cursor="pointer"
                      onClick={() => onClick(period.index)}
                      position="relative" // Needed for connector
                    >
                      <motion.div
                        whileHover={{ scale: 1.05, zIndex: 10 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                      >
                        <Box
                          bg={isMain ? periodBgMain : periodBgBase}
                          borderWidth="1px"
                          borderColor={isMain ? periodBorderMain : periodBorderBase}
                          borderRadius={period.isPoint ? "full" : "lg"}
                          px={period.isPoint ? 0 : 2}
                          py={period.isPoint ? 0 : 1}
                          textAlign="center"
                          position="relative"
                          boxShadow={isMain ? "lg" : "md"}
                          minW={period.isPoint ? "36px" : "60px"}
                          h={period.isPoint ? "36px" : "auto"}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          overflow="hidden"
                        >
                          {period.isPoint ? (
                            <Tooltip label={formatYear(period.startYear)} placement="top" hasArrow>
                               <Icon as={CalendarBlank} boxSize={5} color={isMain ? accentColor : textColorDefault} />
                            </Tooltip>
                          ) : (
                            <VStack spacing={0} align="center" py={0.5}>
                              <Text
                                fontSize="11px"
                                fontWeight={isMain ? "bold" : "medium"}
                                color={textColorDefault}
                                noOfLines={1}
                                px={1}
                                title={period.periodName || "Unnamed Period"} // Add title for full name on hover
                              >
                                {period.periodName || "Unnamed Period"}
                              </Text>
                            </VStack>
                          )}
                          {isMain && (
                            <Circle size="18px" bg={accentColor} position="absolute" top="-8px" right="-8px" boxShadow="md" zIndex={4}>
                              <Star size={10} weight="fill" color="white" />
                            </Circle>
                          )}
                        </Box>
                      </motion.div>
                      {/* Vertical connector line removed based on feedback */}
                    </Box>
                  </PopoverTrigger>
                  {/* Popover shows details on hover */}
                  <PopoverContent maxWidth="250px" _focus={{ boxShadow: 'none' }}>
                    <PopoverArrow bg={useColorModeValue('gray.700', 'gray.300')} />
                    <PopoverBody fontSize="sm" py={2} px={3} bg={useColorModeValue('gray.700', 'gray.300')} color={useColorModeValue('white', 'black')} borderRadius="md">
                      <Text fontWeight="bold" mb={1}>{period.periodName || "Unnamed Period"}</Text>
                      <Text fontSize="xs">
                        {formatYear(period.startYear)} - {period.endEra && period.endEra.toLowerCase() === 'present' ? 'Present' : formatYear(period.endYear)}
                      </Text>
                      {period.culture && (
                        <Text fontSize="xs" fontStyle="italic" mt={1}>
                          Culture: {period.culture}
                        </Text>
                      )}
                      {/* Features for Horizontal Timeline Popover */}
                      {period.features && period.features.length > 0 && (
                        <Flex wrap="wrap" mt={2} gap={1}>
                          {period.features.map((feature, fIndex) => (
                            <Badge
                              key={fIndex}
                              variant="subtle"
                              colorScheme="yellow" // Match theme color scheme
                              fontSize="10px"
                              px={1.5}
                              py={0.5}
                            >
                              {feature}
                            </Badge>
                          ))}
                        </Flex>
                      )}
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </Box>
    </Box>
  );
};


// --- Vertical Timeline (Mobile) ---
const VerticalTimeline = ({ periods, mainPeriodIndex, onClick }) => {
  const timelineColor = useColorModeValue('gray.300', 'gray.600');
  const accentColor = useColorModeValue('brand.primary', 'brand.primary');
  const periodBgBase = useColorModeValue('white', 'gray.700');
  const periodBgMain = useColorModeValue('rgba(193, 148, 52, 0.1)', 'rgba(214, 173, 96, 0.1)');
  const periodBorderBase = useColorModeValue('gray.200', 'gray.600');
  const periodBorderMain = useColorModeValue('rgba(193, 148, 52, 0.3)', 'rgba(214, 173, 96, 0.3)');
  const textColorMuted = useColorModeValue('text.muted', 'text.muted');
  const textColorDefault = useColorModeValue('text.default', 'text.default');

  return (
    <Box position="relative" px={4} py={6}>
      {/* Central Vertical Line */}
      <Box
        position="absolute"
        left="50%"
        top={0}
        bottom={0}
        width="3px"
        bg={timelineColor}
        transform="translateX(-50%)"
        borderRadius="full"
      />

      <VStack spacing={8} align="stretch">
        {periods.map((period, index) => {
          const isMain = index === mainPeriodIndex;
          const isLeft = index % 2 === 0; // Alternate sides

          return (
            <Flex
              key={`${index}-${period.periodName}`}
              direction={isLeft ? 'row' : 'row-reverse'}
              align="center"
              position="relative"
              w="100%"
            >
              {/* Period Card */}
              <Box
                w="calc(50% - 20px)" // Width minus spacing from center line
                cursor="pointer"
                onClick={() => onClick(index)}
              >
                <Tooltip label={`Edit: ${period.periodName || "Unnamed Period"}`} placement={isLeft ? "right" : "left"} hasArrow>
                  <Box
                    bg={isMain ? periodBgMain : periodBgBase}
                    borderWidth="1px"
                    borderColor={isMain ? periodBorderMain : periodBorderBase}
                    borderRadius="lg" // Slightly larger radius
                    p={3} // More padding
                    position="relative"
                    boxShadow={isMain ? "lg" : "md"}
                    _hover={{ boxShadow: "xl", transform: 'translateY(-2px)' }}
                    transition="all 0.2s ease-in-out"
                  >
                    <VStack spacing={1} align="stretch">
                      <Text
                        fontSize="sm" // Slightly larger font
                        fontWeight={isMain ? "bold" : "semibold"}
                        color={textColorDefault}
                        noOfLines={2} // Allow two lines for name
                      >
                        {period.periodName || "Unnamed Period"}
                      </Text>
                      {(period.startEra || period.endEra) && (
                        <Text fontSize="xs" color={textColorMuted} noOfLines={1}>
                          <Icon as={CalendarBlank} mr={1} verticalAlign="middle" />
                          {period.startEra || '?'} - {period.endEra && period.endEra.toLowerCase() === 'present' ? 'Present' : period.endEra || '?'}
                        </Text>
                      )}
                      {period.culture && (
                         <Text fontSize="xs" fontStyle="italic" color={textColorMuted}>
                           {period.culture}
                         </Text>
                      )}
                      {/* Features for Vertical Timeline Card */}
                      {period.features && period.features.length > 0 && (
                        <Flex wrap="wrap" mt={2} gap={1}>
                          {period.features.map((feature, fIndex) => (
                            <Badge
                              key={fIndex}
                              variant="subtle"
                              colorScheme="yellow" // Match theme color scheme
                              fontSize="10px"
                              px={1.5}
                              py={0.5}
                            >
                              {feature}
                            </Badge>
                          ))}
                        </Flex>
                      )}
                    </VStack>
                    {isMain && (
                      <Circle size="20px" bg={accentColor} position="absolute" top="-10px" right="-10px" boxShadow="md">
                        <Star size={12} weight="fill" color="white" />
                      </Circle>
                    )}
                  </Box>
                </Tooltip>
              </Box>

              {/* Connector Dot on Timeline */}
              <Circle
                size="12px"
                bg={isMain ? accentColor : timelineColor}
                position="absolute"
                left="50%"
                transform="translateX(-50%)"
                border="2px solid"
                borderColor={useColorModeValue('white', 'gray.800')} // Match background for cutout effect
                zIndex={1}
              />

              {/* Horizontal Connector Line */}
              <Box
                position="absolute"
                top="50%"
                left={isLeft ? 'calc(50% - 20px + 6px)' : '20px'} // Adjust based on side
                right={isLeft ? 'auto' : 'calc(50% - 20px + 6px)'}
                width="calc(50% - 20px - 6px)" // Width from card edge to center dot edge
                height="2px"
                bg={timelineColor}
                transform="translateY(-50%)"
                zIndex={0}
              />
            </Flex>
          );
        })}
      </VStack>
    </Box>
  );
};


/**
 * VisualTimeline component for displaying construction periods in a visual timeline format
 * Switches between Horizontal (desktop) and Vertical (mobile) layouts.
 *
 * @param {Object[]} periods - Array of period objects to display
 * @param {number} mainPeriodIndex - Index of the main period in the periods array
 * @param {Function} onClick - Callback function when a period is clicked, receives the index as parameter
 */
const VisualTimeline = ({ periods, mainPeriodIndex, onClick }) => {
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Skip rendering if no periods
  if (!periods || periods.length === 0) return null;

  return isMobile ? (
    <VerticalTimeline periods={periods} mainPeriodIndex={mainPeriodIndex} onClick={onClick} />
  ) : (
    <HorizontalTimeline periods={periods} mainPeriodIndex={mainPeriodIndex} onClick={onClick} />
  );
};

export default VisualTimeline;
